import React, { useState } from 'react';
import { Form, Input, Button, Typography, message } from 'antd';
import { MailOutlined, LockOutlined, UserOutlined } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import { register } from './authApi';
import { setUser } from './authSlice';
import { useDispatch } from 'react-redux';
import { persistAuthTokens } from '../../services/authTokens';

const { Title, Text } = Typography;

export default function RegisterPage() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const result = await register({
        email: values.email,
        password: values.password,
        displayName: values.displayName,
      });
      persistAuthTokens(result);
      dispatch(setUser(result.user));
      message.success('Account created successfully!');
      navigate('/');
    } catch (err) {
      message.error(err.response?.data?.title || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="df-auth-shell">
      <div className="df-auth-panel">
        <div className="df-auth-panel__inner">
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div className="df-brand df-brand--dark" style={{ justifyContent: 'center', marginBottom: 18 }}>
              <span className="df-brand__mark" aria-hidden="true">DF</span>
              <span className="df-brand__wordmark">DealFusion</span>
            </div>
            <Title level={3} style={{ margin: 0, fontFamily: 'var(--df-font-display)' }}>
              Join DealFusion
            </Title>
            <Text style={{ fontSize: 14, marginTop: 6, display: 'block', color: 'var(--df-text-secondary)' }}>
              Create an account to submit deals, vote, and set alerts.
            </Text>
          </div>

          <Form layout="vertical" onFinish={handleSubmit} size="large" requiredMark={false}>
            <Form.Item
              name="displayName"
              label="Display name"
              rules={[{ required: true, min: 2, max: 100, message: 'Enter a display name (2–100 chars)' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: 'var(--df-text-muted)' }} />}
                placeholder="Alex Johnson"
                style={{ borderRadius: 8, height: 44 }}
              />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                { required: true, message: 'Enter your email' },
                { type: 'email', message: 'Invalid email format' },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: 'var(--df-text-muted)' }} />}
                placeholder="you@email.com"
                style={{ borderRadius: 8, height: 44 }}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, min: 8, message: 'Password must be at least 8 characters' }]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'var(--df-text-muted)' }} />}
                placeholder="Create a password"
                style={{ borderRadius: 8, height: 44 }}
              />
            </Form.Item>

            <Form.Item
              name="confirm"
              label="Confirm password"
              dependencies={['password']}
              hasFeedback
              rules={[
                { required: true, message: 'Confirm your password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) return Promise.resolve();
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'var(--df-text-muted)' }} />}
                placeholder="Confirm your password"
                style={{ borderRadius: 8, height: 44 }}
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                style={{
                  borderRadius: 8,
                  height: 46,
                  fontWeight: 600,
                  background: 'var(--df-accent)',
                  borderColor: 'var(--df-accent)',
                }}
              >
                Create Account
              </Button>
            </Form.Item>
          </Form>

          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <Text style={{ fontSize: 13, color: 'var(--df-text-secondary)' }}>
              Already have an account?{' '}
              <Link to="/login" style={{ fontWeight: 700, color: 'var(--df-primary)' }}>Sign in</Link>
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}
